import {Link, useSearchParams} from 'react-router-dom';
import {useFeed} from '../../api/posts';
import Button from '../Button';
import ErrorBox from '../feedback/ErrorBox';
import {SkeletonList} from '../feedback/Skeleton';
import Icon from '../Icon';
import Lifecycle from './Lifecycle';
import TellRow from '../tell/TellRow';
import {groupByDay} from '../../lib/groupByDay';
import {deriveLifecycle} from '../../lib/lifecycle';
import {formatDayMonth, todayLocal} from '../../lib/when';
import {FeedFilters, PlantSeason} from '../../types';

// The season view a plant page and a species page share: season chips, a summary line, the
// season's tells as the lifecycle (a crop) or day by day (a permanent plant), and the season
// before for comparison. The parent holds the state (useSeasonFeed) so it can use the posts too.

const tellsWord = (n: number) => `${n} ${n === 1 ? 'tell' : 'tells'}`;

const lastSeasonLine = (s: PlantSeason): string =>
    [`${s.key} · ${tellsWord(s.tells)}`, s.firstHarvestAt ? `first harvest ${formatDayMonth(s.firstHarvestAt)}` : null, s.quantitySample ? `“${s.quantitySample}”` : null].filter(Boolean).join(' · ');

/** The chosen season (?season=, else the newest), the one before it, and its tells. */
export function useSeasonFeed(seasons: PlantSeason[], filter: FeedFilters) {
    const [params, setParams] = useSearchParams();
    const wanted = params.get('season');
    const index = Math.max(0, seasons.findIndex(s => s.key === wanted));
    const season = seasons[index] ?? null;
    const previous = seasons[index + 1] ?? null;
    const feed = useFeed(season ? {...filter, from: season.from, to: season.to} : filter);
    const posts = season ? (feed.data?.pages.flatMap(p => p.items) ?? []) : [];
    const pick = (key: string) => setParams(key === seasons[0]?.key ? {} : {season: key}, {replace: true});
    return {seasons, season, previous, feed, posts, pick};
}

type Props = {
    state: ReturnType<typeof useSeasonFeed>;
    isPermanent: boolean;
    /** The line under the chips: variety, where, when it started, how many tells. */
    summary: (season: PlantSeason) => string;
    /** The place of the first tell with one, linked where it appears in the summary. */
    placeLink?: {id: string; name: string} | null;
};

export default function SeasonStory({state, isPermanent, summary, placeLink}: Props) {
    const {seasons, season, previous, feed, posts, pick} = state;
    const lifecycle = !isPermanent && season ? deriveLifecycle(posts, previous) : null;

    return (
        <>
            {seasons.length > 0 && (
                <div className="-mx-4 flex gap-2 overflow-x-auto px-4 py-1 [scrollbar-width:none]" role="group" aria-label="Season">
                    {seasons.map(s => (
                        <button key={s.key} type="button" className="chip shrink-0" aria-pressed={s.key === season?.key} onClick={() => pick(s.key)}>
                            {s.key}
                        </button>
                    ))}
                </div>
            )}

            {season && (
                <p className="meta -mt-2 m-0">
                    {summary(season)}
                    {placeLink && (
                        <>
                            {' · '}
                            <Link to={`/places/${placeLink.id}`} className="font-semibold text-shoot-text no-underline">
                                {placeLink.name}
                            </Link>
                        </>
                    )}
                </p>
            )}

            {season && feed.isPending && <SkeletonList rows={4} />}
            {season && feed.isError && <ErrorBox error={feed.error} onRetry={() => void feed.refetch()} />}

            {lifecycle && feed.isSuccess && (
                <>
                    <Lifecycle steps={lifecycle.steps} />
                    {lifecycle.other.length > 0 && (
                        <section className="flex flex-col gap-2">
                            <h2 className="band-label m-0 text-ink-2">Also this season</h2>
                            <div className="rows card px-4">
                                {lifecycle.other.map(post => (
                                    <TellRow key={post.id} post={post} to={`/tell/${post.id}`} />
                                ))}
                            </div>
                        </section>
                    )}
                </>
            )}

            {isPermanent &&
                season &&
                feed.isSuccess &&
                groupByDay(posts, todayLocal()).map(group => (
                    <section key={group.day} className="flex flex-col gap-2">
                        <h2 className="m-0 font-display text-lg font-bold">{group.label}</h2>
                        <div className="rows card px-4">
                            {group.posts.map(post => (
                                <TellRow key={post.id} post={post} to={`/tell/${post.id}`} />
                            ))}
                        </div>
                    </section>
                ))}

            {season && feed.hasNextPage && (
                <Button variant="ghost" busy={feed.isFetchingNextPage} busyLabel="Loading…" onClick={() => feed.fetchNextPage()}>
                    Show older
                </Button>
            )}

            {previous && (
                <section className="flex flex-col gap-2">
                    <h2 className="band-label m-0 text-ink-2">Last season, same plant</h2>
                    <div className="rows rounded-[18px] border-[1.5px] border-line bg-surface">
                        <button type="button" onClick={() => pick(previous.key)} className="flex min-h-[56px] w-full items-center gap-3 px-4 text-left">
                            <Icon name="calendar" size={20} className="text-shoot-text" />
                            <span className="flex-1 font-[650]">{lastSeasonLine(previous)}</span>
                            <Icon name="chevron" size={20} className="text-ink-3" />
                        </button>
                    </div>
                </section>
            )}
        </>
    );
}
